import { useState, type ReactNode } from 'react';
import type { components } from '@adiox/api';
import { BottomSheet } from '../mobile/BottomSheet';
import { useSymbols } from '../market/useSymbols';
import { useWorkspace } from '../workspace/workspaceStore';
import { PRESETS, type Preset } from './range';
import type { HistorySummary } from './summary';

type Deal = components['schemas']['DealRecord'];
type Order = components['schemas']['OrderRecord'];
type Position = components['schemas']['ClosedPosition'];

export type MobileHistoryTab = 'positions' | 'orders' | 'deals';
const TABS: { id: MobileHistoryTab; label: string }[] = [
  { id: 'positions', label: 'Positions' },
  { id: 'orders', label: 'Orders' },
  { id: 'deals', label: 'Deals' },
];

const tone = (v: number) => (v > 0 ? 'text-up' : v < 0 ? 'text-down' : '');
const kindOf = (type: string) => type.replace(/([a-z])([A-Z])/g, '$1 $2').toLowerCase();
const row = 'flex w-full items-start justify-between gap-3 border-b border-line px-4 py-3 text-left';
const sheetAction =
  'block min-h-14 w-full border-t border-line px-4 py-4 text-center text-lg hover:bg-panel2 disabled:opacity-40';

function Detail({ label, children }: { label: string; children: ReactNode }) {
  return (
    <span>
      <span className="text-muted">{label}: </span>
      <span className="num">{children}</span>
    </span>
  );
}

const SortIcon = () => (
  <svg
    width="22"
    height="22"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    aria-hidden="true"
  >
    <path d="M6 4v16M3 7l3-3 3 3M3 17l3 3 3-3M13 6h8M13 12h8M13 18h8" />
  </svg>
);
const PeriodIcon = () => (
  <svg
    width="22"
    height="22"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    aria-hidden="true"
  >
    <circle cx="12" cy="13" r="8" />
    <path d="M12 9v4l3 2M9 2h6M12 2v3" />
  </svg>
);

/**
 * The History screen on a phone, laid out as MetaTrader's phone terminal does it: Positions / Orders / Deals along the top
 * with sort and period buttons, the period's totals in two columns, then one two-line entry per position, order or deal. A
 * tap on an entry opens its details from the bottom, with a shortcut to its chart. The data comes from the History panel.
 */
export function MobileHistoryView({
  tab,
  onTab,
  rows,
  busy,
  failed,
  onRetry,
  hasNext,
  fetchingNext,
  onMore,
  summary,
  balance,
  preset,
  onPreset,
  custom,
  onCustom,
  problem,
  symbol,
  onSymbol,
  fmt,
  time,
}: {
  tab: MobileHistoryTab;
  onTab: (tab: MobileHistoryTab) => void;
  rows: unknown[];
  busy: boolean;
  failed: boolean;
  onRetry: () => void;
  hasNext: boolean;
  fetchingNext: boolean;
  onMore: () => void;
  summary: HistorySummary | undefined;
  balance: number | null;
  preset: Preset;
  onPreset: (preset: Preset) => void;
  custom: { from: string; to: string };
  onCustom: (range: { from: string; to: string }) => void;
  problem: string | null;
  symbol: string;
  onSymbol: (symbol: string) => void;
  fmt: (v: number) => string;
  time: (iso: string | null | undefined) => string;
}) {
  const symbols = useSymbols().data;
  const [newestFirst, setNewestFirst] = useState(true);
  const [filter, setFilter] = useState(false);
  const [picked, setPicked] = useState<unknown | null>(null);
  const digitsOf = (name: string) => symbols?.find((s) => s.name === name)?.digits ?? 5;
  const stamp = (iso: string | null | undefined) => time(iso).replaceAll('-', '.'); // 2026.10.01 14:44:59
  const shown = newestFirst ? rows : [...rows].reverse();
  const showChart = (name: string) => {
    setPicked(null);
    useWorkspace.getState().actions?.newChart(name);
  };

  const totals: [string, string][] = summary
    ? [
        ['Profit:', fmt(summary.profit)],
        ['Credit:', fmt(summary.credit)],
        ['Deposit:', fmt(summary.deposits)],
        ['Withdrawal:', fmt(summary.withdrawals)],
        ['Balance:', balance === null ? '—' : fmt(balance)],
        ['Commission:', fmt(summary.commission)],
        ['Fee:', fmt(0)],
        ['Swap:', fmt(summary.swap)],
        ['Gross Profit:', fmt(summary.profit)],
      ]
    : [];

  const entry = (
    key: string,
    left: ReactNode,
    below: ReactNode,
    right: ReactNode,
    rightBelow: ReactNode,
    r: unknown,
  ) => (
    <li key={key} data-testid="history-row">
      <button type="button" className={row} aria-haspopup="dialog" onClick={() => setPicked(r)}>
        <span className="min-w-0">
          <span className="block truncate">{left}</span>
          <span className="num block text-sm">{below}</span>
        </span>
        <span className="shrink-0 text-right">
          <span className="num block text-lg font-semibold">{right}</span>
          <span className="num block text-sm">{rightBelow}</span>
        </span>
      </button>
    </li>
  );

  const side = (s: string) => (s === 'Buy' ? 'text-up' : 'text-down');

  const list = shown.map((r, i) => {
    if (tab === 'positions') {
      const p = r as Position;
      const d = digitsOf(p.symbol);
      return entry(
        `p${p.ticket}`,
        <>
          <span className="font-semibold">{p.symbol}</span>{' '}
          <span className={side(p.side)}>
            {p.side.toLowerCase()} {p.volume}
          </span>
        </>,
        <>
          {p.priceOpen.toFixed(d)} → {p.timeClose ? p.priceClose.toFixed(d) : '—'}
        </>,
        <span className={tone(p.profit)}>{fmt(p.profit)}</span>,
        stamp(p.timeClose),
        p,
      );
    }
    if (tab === 'orders') {
      const o = r as Order;
      const d = digitsOf(o.symbol);
      return entry(
        `o${o.ticket}`,
        <>
          <span className="font-semibold">{o.symbol}</span>{' '}
          <span className={o.type.startsWith('Buy') ? 'text-up' : 'text-down'}>
            {kindOf(o.type)} {o.volumeInitial}
          </span>
        </>,
        <>{o.priceOrder.toFixed(d)}</>,
        <span className="text-base font-normal">{o.status.toLowerCase()}</span>,
        stamp(o.timeDone ?? o.timeSetup),
        o,
      );
    }
    const dl = r as Deal;
    if (dl.type === 'Balance' || dl.type === 'Credit') {
      return entry(
        `d${dl.ticket}`,
        <span className="font-semibold">{dl.type}</span>,
        <></>,
        <span className="text-accent">{fmt(dl.profit)}</span>,
        stamp(dl.time),
        dl,
      );
    }
    const d = digitsOf(dl.symbol);
    return entry(
      `d${dl.ticket}-${i}`,
      <>
        <span className="font-semibold">{dl.symbol}</span>{' '}
        <span className={side(dl.type)}>
          {dl.type.toLowerCase()} {dl.volume}
        </span>
      </>,
      <>
        {dl.price.toFixed(d)} <span className="text-muted">{dl.entry === 'In' ? 'in' : 'out'}</span>
      </>,
      dl.entry === 'In' ? (
        <span className="text-muted">—</span>
      ) : (
        <span className={tone(dl.profit)}>{fmt(dl.profit)}</span>
      ),
      stamp(dl.time),
      dl,
    );
  });

  const ctl =
    'w-full rounded-md border border-line bg-panel2 px-3 py-2 text-base outline-none focus:border-accent';

  return (
    <div className="flex h-full flex-col">
      <div className="flex items-center border-b border-line pr-2">
        <div role="tablist" aria-label="History type" className="flex flex-1">
          {TABS.map((t) => (
            <button
              key={t.id}
              type="button"
              role="tab"
              aria-selected={tab === t.id}
              onClick={() => onTab(t.id)}
              className={`border-b-[3px] px-4 py-3.5 text-lg ${
                tab === t.id ? 'border-accent text-fg' : 'border-transparent text-muted'
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>
        <button
          type="button"
          aria-label={newestFirst ? 'Newest first: show oldest first' : 'Oldest first: show newest first'}
          aria-pressed={!newestFirst}
          onClick={() => setNewestFirst(!newestFirst)}
          className="grid h-11 w-11 place-items-center"
        >
          <SortIcon />
        </button>
        <button
          type="button"
          aria-label="Period and symbol"
          aria-haspopup="dialog"
          onClick={() => setFilter(true)}
          className="grid h-11 w-11 place-items-center"
        >
          <PeriodIcon />
        </button>
      </div>

      <div className="min-h-0 flex-1 overflow-auto" tabIndex={0} role="group" aria-label="History list">
        {tab === 'positions' && summary && (
          <dl
            className="grid grid-cols-[auto_1fr_auto_1fr] items-baseline gap-x-3 gap-y-2 border-b border-line px-4 py-3 text-base"
            aria-label="Totals for this period"
            data-testid="history-summary"
          >
            {totals.map(([label, value]) => (
              <div key={label} className="contents">
                <dt className="text-muted">{label}</dt>
                <dd className="num">{value}</dd>
              </div>
            ))}
          </dl>
        )}
        {problem && (
          <p role="alert" className="px-4 py-2 text-sm text-down">
            {problem}
          </p>
        )}
        {busy ? (
          <p className="p-4 text-sm text-muted">Loading…</p>
        ) : failed ? (
          <div role="alert" className="flex items-center gap-3 p-4 text-sm text-down">
            <span>Could not load the history.</span>
            <button
              type="button"
              className="rounded-md border border-line px-3 py-1.5 text-fg"
              onClick={onRetry}
            >
              Retry
            </button>
          </div>
        ) : shown.length === 0 ? (
          <p className="p-4 text-sm text-muted">Nothing in this period.</p>
        ) : (
          <>
            <ul aria-label={TABS.find((t) => t.id === tab)!.label}>{list}</ul>
            {hasNext && (
              <div className="p-3">
                <button
                  type="button"
                  className="min-h-11 w-full rounded-md border border-line px-3 py-2 text-base hover:border-accent disabled:opacity-40"
                  disabled={fetchingNext}
                  onClick={onMore}
                >
                  {fetchingNext ? 'Loading…' : 'Load more'}
                </button>
              </div>
            )}
          </>
        )}
      </div>

      <BottomSheet open={filter} label="Period and symbol" onClose={() => setFilter(false)}>
        <div className="flex flex-col gap-3 px-4 py-4">
          <label className="text-sm text-muted" htmlFor="m-history-range">
            Period
          </label>
          <select
            id="m-history-range"
            className={ctl}
            value={preset}
            onChange={(e) => onPreset(e.target.value as Preset)}
          >
            {PRESETS.map((p) => (
              <option key={p.id} value={p.id}>
                {p.label}
              </option>
            ))}
          </select>
          {preset === 'custom' && (
            <div className="flex items-center gap-2">
              <label className="sr-only" htmlFor="m-history-from">
                From date
              </label>
              <input
                id="m-history-from"
                type="date"
                className={ctl}
                value={custom.from}
                onChange={(e) => onCustom({ ...custom, from: e.target.value })}
              />
              <span className="text-muted">to</span>
              <label className="sr-only" htmlFor="m-history-to">
                To date
              </label>
              <input
                id="m-history-to"
                type="date"
                className={ctl}
                value={custom.to}
                onChange={(e) => onCustom({ ...custom, to: e.target.value })}
              />
            </div>
          )}
          <label className="text-sm text-muted" htmlFor="m-history-symbol">
            Symbol
          </label>
          <select
            id="m-history-symbol"
            className={ctl}
            value={symbol}
            onChange={(e) => onSymbol(e.target.value)}
          >
            <option value="">All symbols</option>
            {(symbols ?? []).map((s) => (
              <option key={s.name} value={s.name}>
                {s.name}
              </option>
            ))}
          </select>
        </div>
        <button type="button" className={sheetAction} onClick={() => setFilter(false)}>
          Done
        </button>
      </BottomSheet>

      <BottomSheet open={picked !== null} label="Details" onClose={() => setPicked(null)}>
        {picked !== null && (
          <HistoryDetails
            tab={tab}
            item={picked}
            digitsOf={digitsOf}
            fmt={fmt}
            stamp={stamp}
            onChart={showChart}
            action={sheetAction}
          />
        )}
      </BottomSheet>
    </div>
  );
}

function HistoryDetails({
  tab,
  item,
  digitsOf,
  fmt,
  stamp,
  onChart,
  action,
}: {
  tab: MobileHistoryTab;
  item: unknown;
  digitsOf: (symbol: string) => number;
  fmt: (v: number) => string;
  stamp: (iso: string | null | undefined) => string;
  onChart: (symbol: string) => void;
  action: string;
}) {
  const head = 'flex flex-col gap-2 px-4 py-4';
  if (tab === 'positions') {
    const p = item as Position;
    const d = digitsOf(p.symbol);
    return (
      <div data-testid="history-sheet">
        <div className={head}>
          <p className="num text-muted">#{p.ticket}</p>
          <p className="num text-muted">
            {stamp(p.timeOpen)} → {stamp(p.timeClose)}
          </p>
          <div className="flex items-baseline justify-between">
            <span className="text-lg">
              <span className="font-semibold">{p.symbol}</span>{' '}
              <span className={p.side === 'Buy' ? 'text-up' : 'text-down'}>{p.side.toLowerCase()}</span>{' '}
              <span className="num text-accent">{p.volume}</span>
            </span>
            <span className={`num text-xl font-semibold ${tone(p.profit)}`}>{fmt(p.profit)}</span>
          </div>
          <p className="num text-lg">
            {p.priceOpen.toFixed(d)} → {p.timeClose ? p.priceClose.toFixed(d) : '—'}
          </p>
          <div className="flex justify-between">
            <Detail label="S / L">{p.stopLoss > 0 ? p.stopLoss.toFixed(d) : '-'}</Detail>
            <Detail label="Swap">{Number(p.swap.toFixed(2))}</Detail>
          </div>
          <div className="flex justify-between">
            <Detail label="T / P">{p.takeProfit > 0 ? p.takeProfit.toFixed(d) : '-'}</Detail>
            <Detail label="Charges">{p.commission !== 0 ? fmt(p.commission) : '-'}</Detail>
          </div>
        </div>
        <button type="button" className={action} onClick={() => onChart(p.symbol)}>
          Chart
        </button>
      </div>
    );
  }
  if (tab === 'orders') {
    const o = item as Order;
    const d = digitsOf(o.symbol);
    return (
      <div data-testid="history-sheet">
        <div className={head}>
          <p className="num text-muted">#{o.ticket}</p>
          <p className="num text-muted">
            {stamp(o.timeSetup)} → {stamp(o.timeDone)}
          </p>
          <div className="flex items-baseline justify-between">
            <span className="text-lg">
              <span className="font-semibold">{o.symbol}</span>{' '}
              <span className={o.type.startsWith('Buy') ? 'text-up' : 'text-down'}>{kindOf(o.type)}</span>{' '}
              <span className="num text-accent">{o.volumeInitial}</span>
            </span>
            <span className="text-lg">{o.status.toLowerCase()}</span>
          </div>
          <p className="num text-lg">{o.priceOrder.toFixed(d)}</p>
          <div className="flex justify-between">
            <Detail label="S / L">{o.stopLoss > 0 ? o.stopLoss.toFixed(d) : '-'}</Detail>
            <Detail label="Filled">{Number((o.volumeInitial - o.volumeCurrent).toFixed(2))}</Detail>
          </div>
          <div className="flex justify-between">
            <Detail label="T / P">{o.takeProfit > 0 ? o.takeProfit.toFixed(d) : '-'}</Detail>
          </div>
        </div>
        <button type="button" className={action} onClick={() => onChart(o.symbol)}>
          Chart
        </button>
      </div>
    );
  }
  const dl = item as Deal;
  const d = digitsOf(dl.symbol);
  const money = dl.type === 'Balance' || dl.type === 'Credit';
  return (
    <div data-testid="history-sheet">
      <div className={head}>
        <p className="num text-muted">#{dl.ticket}</p>
        <p className="num text-muted">{stamp(dl.time)}</p>
        <div className="flex items-baseline justify-between">
          <span className="text-lg">
            <span className="font-semibold">{money ? dl.type : dl.symbol}</span>
            {!money && (
              <>
                {' '}
                <span className={dl.type === 'Buy' ? 'text-up' : 'text-down'}>
                  {dl.type.toLowerCase()}
                </span>{' '}
                <span className="num text-accent">{dl.volume}</span>
              </>
            )}
          </span>
          <span className={`num text-xl font-semibold ${money ? 'text-accent' : tone(dl.profit)}`}>
            {fmt(dl.profit)}
          </span>
        </div>
        {!money && (
          <>
            <p className="num text-lg">
              {dl.price.toFixed(d)} <span className="text-muted">{dl.entry === 'In' ? 'in' : 'out'}</span>
            </p>
            <div className="flex justify-between">
              <Detail label="Commission">{fmt(dl.commission)}</Detail>
              <Detail label="Swap">{fmt(dl.swap)}</Detail>
            </div>
            <div className="flex justify-between">
              <Detail label="Position">{dl.positionId}</Detail>
              <Detail label="Reason">{dl.reason.toLowerCase()}</Detail>
            </div>
          </>
        )}
        {dl.comment && <p className="text-muted">{dl.comment}</p>}
      </div>
      {!money && (
        <button type="button" className={action} onClick={() => onChart(dl.symbol)}>
          Chart
        </button>
      )}
    </div>
  );
}
